import { Minus, Plus, Trash2, Package } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { TableCell, TableRow } from '@/components/ui/table'
import { OrderModel } from '@/types/model/Order'
import { getFileUrl } from '@/clients/http'
import { TextInput } from '@/components/atoms'
import { DataType } from '@/utils'
import { useBusiness } from '@/contexts/BusinessContext'
import { cn } from '@/lib/utils'

interface OrderItemRowProps {
    item: OrderModel.OrderItem
    loading: boolean
    onUpdateQuantity: (id: string, qty: number) => void
    onUpdateDiscount: (id: string, discount: number) => void
    onRemove: (id: string) => void
    isEditable?: boolean
}

interface ItemDiscountFormValues {
    discount: number
}

export const OrderItemRow = ({
    item,
    loading,
    onUpdateQuantity,
    onUpdateDiscount,
    onRemove,
    isEditable = true,
}: OrderItemRowProps) => {
    const { formatCurrency, currencySymbol } = useBusiness()
    const [isOpenDiscountDesktop, setIsOpenDiscountDesktop] = useState(false)
    const [isOpenDiscountMobile, setIsOpenDiscountMobile] = useState(false)

    const form = useForm<ItemDiscountFormValues>({
        defaultValues: {
            discount: item.discount,
        },
    })

    useEffect(() => {
        if (isOpenDiscountDesktop || isOpenDiscountMobile) {
            form.reset({
                discount: item.discount,
            })
        }
    }, [isOpenDiscountDesktop, isOpenDiscountMobile, item.discount, form])

    const handleQuantityChange = (newQty: number) => {
        if (newQty < 1) return
        onUpdateQuantity(item._id, newQty)
    }

    const onSubmit = (data: ItemDiscountFormValues) => {
        onUpdateDiscount(item._id, DataType.number(data.discount))
        setIsOpenDiscountDesktop(false)
        setIsOpenDiscountMobile(false)
    }

    const subtotal = item.unit_price * item.quantity
    const discountAmount = item.discount_type === 'percentage'
        ? (subtotal * item.discount) / 100
        : item.discount
    const total = subtotal - discountAmount

    const discountLabel = `- ${formatCurrency(item?.discount)}`

    const thumbnail = (
        <div className="size-10 shrink-0 overflow-hidden rounded-md border border-border bg-subdued">
            {item.product_info?.thumbnail ? (
                <img
                    src={getFileUrl(item.product_info.thumbnail.path)}
                    alt={item.product_info.name}
                    className="size-full object-cover"
                />
            ) : (
                <div className="flex size-full items-center justify-center">
                    <Package className="size-4 text-muted-foreground" />
                </div>
            )}
        </div>
    )

    const quantityStepper = (
        <div className="flex w-fit items-center gap-1 rounded-md border border-border bg-card p-0.5">
            {isEditable && (
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Decrease quantity"
                    onClick={() => handleQuantityChange(item.quantity - 1)}
                    disabled={loading || item.quantity <= 1}
                >
                    <Minus />
                </Button>
            )}
            <span className="w-6 text-center text-sm tabular-nums text-ink">{item.quantity}</span>
            {isEditable && (
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Increase quantity"
                    onClick={() => handleQuantityChange(item.quantity + 1)}
                    disabled={loading}
                >
                    <Plus />
                </Button>
            )}
        </div>
    )

    const discountForm = (
        <div className="space-y-3">
            <TextInput
                control={form.control}
                name="discount"
                label={`Discount (${currencySymbol})`}
                type="number"
                placeholder="0.00"
                errors={form.formState.errors}
            />
            <Button className="w-full" onClick={form.handleSubmit(onSubmit)} disabled={loading}>
                Apply
            </Button>
        </div>
    )

    return (
        <>
            {/* Desktop View */}
            <TableRow className="hidden md:table-row">
                <TableCell>
                    <div className="flex items-center gap-3">
                        {thumbnail}
                        <div className="min-w-0">
                            <p className="max-w-[250px] truncate text-sm font-medium text-ink">{item.product_info?.name}</p>
                            {item.product_info?.variation_name && (
                                <p className="text-xs text-muted-foreground">{item.product_info?.variation_name}</p>
                            )}
                        </div>
                    </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-right tabular-nums">{formatCurrency(item.unit_price)}</TableCell>
                <TableCell>{quantityStepper}</TableCell>
                <TableCell className="text-right">
                    {isEditable ? (
                        <Popover open={isOpenDiscountDesktop} onOpenChange={setIsOpenDiscountDesktop}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn('tabular-nums', item.discount > 0 ? 'text-error' : 'text-muted-foreground')}
                                >
                                    {discountLabel}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-4">
                                <p className="mb-3 text-sm font-semibold text-ink">Item discount</p>
                                {discountForm}
                            </PopoverContent>
                        </Popover>
                    ) : (
                        <span className={cn('text-sm tabular-nums', item.discount > 0 ? 'text-error' : 'text-muted-foreground')}>
                            {discountLabel}
                        </span>
                    )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right font-medium tabular-nums">
                    {formatCurrency(total)}
                </TableCell>
                <TableCell className="text-right">
                    {isEditable && (
                        <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Remove item"
                            onClick={() => onRemove(item._id)}
                            disabled={loading}
                            className="text-muted-foreground hover:text-error"
                        >
                            <Trash2 />
                        </Button>
                    )}
                </TableCell>
            </TableRow>

            {/* Mobile View - Card Layout */}
            <TableRow className="hover:bg-transparent md:hidden">
                <TableCell colSpan={6} className="px-4 py-3">
                    <div className="space-y-3">
                        <div className="flex gap-3">
                            {thumbnail}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="line-clamp-2 text-sm font-medium text-ink">{item.product_info?.name}</p>
                                    {isEditable && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            aria-label="Remove item"
                                            onClick={() => onRemove(item._id)}
                                            disabled={loading}
                                            className="text-muted-foreground"
                                        >
                                            <Trash2 />
                                        </Button>
                                    )}
                                </div>
                                {item.product_info?.variation_name && (
                                    <p className="text-xs text-muted-foreground">{item.product_info?.variation_name}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <p className="text-sm tabular-nums text-text-2">{formatCurrency(item.unit_price)}</p>
                            {quantityStepper}
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs text-muted-foreground">Discount</p>
                                {isEditable ? (
                                    <Popover open={isOpenDiscountMobile} onOpenChange={setIsOpenDiscountMobile}>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                className={cn(
                                                    'text-sm tabular-nums underline decoration-dotted underline-offset-4',
                                                    item.discount > 0 ? 'text-error' : 'text-muted-foreground'
                                                )}
                                            >
                                                {discountLabel}
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-64 p-4" side="bottom">
                                            {discountForm}
                                        </PopoverContent>
                                    </Popover>
                                ) : (
                                    <p className="text-sm tabular-nums text-muted-foreground">{discountLabel}</p>
                                )}
                            </div>

                            <div className="text-right">
                                <p className="text-xs text-muted-foreground">Total</p>
                                <p className="text-sm font-semibold tabular-nums text-ink">{formatCurrency(total)}</p>
                            </div>
                        </div>
                    </div>
                </TableCell>
            </TableRow>
        </>
    )
}
